/**
 * Knowledge Base Page - Instruction Manual
 * Comprehensive guide for using the Avida Compliance Platform
 */
import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Book, 
  Users, 
  Calendar, 
  FileText, 
  AlertTriangle, 
  ClipboardCheck,
  Settings,
  Shield,
  ChevronDown,
  ChevronRight,
  Search,
  Home,
  Upload,
  Download,
  UserPlus,
  Eye,
  CheckCircle,
  XCircle,
  Bell
} from 'lucide-react';
import { Input } from '../components/ui/input';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Home,
    content: [
      {
        title: 'Logging In',
        body: `1. Navigate to the login page
2. Enter your email address and password
3. Click the eye icon (👁) to verify your password if needed
4. Click "Sign In"

**First Login:** If this is your first time logging in, please change your password immediately via the User Management page or contact your administrator.`
      },
      {
        title: 'Understanding Your Role',
        body: `Your access level depends on your assigned role:

**Admin** - Full access to all features including user management, system settings, and audit logs

**Compliance Manager** - Access to compliance features, survey readiness, document management, and read-only system settings

**Scheduler** - Access to client management, scheduling, EVV inbox, and documents

**Caregiver** - Limited access to view assigned schedules and relevant information`
      },
      {
        title: 'Dashboard Overview',
        body: `The dashboard provides a quick overview of your agency's status:

• **System Status** - Shows application health and database connection
• **Quick Stats** - Displays counts for clients, users, visits, and documents
• **Recent Activity** - Shows recent actions in the system
• **Compliance Alerts** - Highlights items needing attention`
      }
    ]
  },
  {
    id: 'clients',
    title: 'Client Management',
    icon: Users,
    content: [
      {
        title: 'Viewing Clients',
        body: `Navigate to **Clients** in the sidebar to see all clients.

• Use the search bar to find specific clients
• Filter by status (Active, Inactive, Pending)
• Click on a client row to view details`
      },
      {
        title: 'Adding a New Client',
        body: `1. Click the **"Add Client"** button
2. Fill in required information:
   - First Name, Last Name
   - Date of Birth
   - MA Number (Medicaid ID)
   - Address and contact information
3. Set the initial status (typically "Pending")
4. Click **"Save"**

**Note:** New clients start in "Pending" status until all required documentation is uploaded.`
      },
      {
        title: 'Client Documents',
        body: `Each client needs specific documents for compliance:

• **Care Plan** - Required, must be current
• **Physician Orders** - Required
• **Assessment** - Required
• **Emergency Contact Form** - Required
• **Consent Forms** - Required

Upload documents via the client detail page or the Documents section.`
      },
      {
        title: 'Archiving Clients',
        body: `To archive a client (instead of deleting):

1. Open the client's detail page
2. Click **"Archive"** button
3. Confirm the action

**Note:** Archived clients are retained for compliance records but won't appear in active lists.`
      }
    ]
  },
  {
    id: 'scheduling',
    title: 'Scheduling',
    icon: Calendar,
    content: [
      {
        title: 'Viewing the Schedule',
        body: `Navigate to **Schedule** to see all visits.

• **Calendar View** - See visits by day, week, or month
• **List View** - See visits in a table format
• Filter by client, caregiver, or date range`
      },
      {
        title: 'Creating a Visit',
        body: `1. Click **"Add Visit"** or click on a calendar date
2. Select the **Client**
3. Select the **Caregiver**
4. Set **Start Time** and **End Time**
5. Add any notes if needed
6. Click **"Save"**

**Validation:** The system checks that:
- Client is Active
- Caregiver is assigned and available
- No scheduling conflicts exist`
      },
      {
        title: 'Visit Status',
        body: `Visits can have the following statuses:

• **Scheduled** - Upcoming visit
• **In Progress** - Visit currently happening
• **Completed** - Visit finished with documentation
• **Missed** - Visit did not occur
• **Cancelled** - Visit was cancelled`
      }
    ]
  },
  {
    id: 'evv',
    title: 'EVV Management',
    icon: AlertTriangle,
    content: [
      {
        title: 'Understanding EVV',
        body: `**Electronic Visit Verification (EVV)** is required by Wisconsin DHS for all personal care services.

EVV data comes from **Sandata** and includes:
• Check-in and check-out times
• GPS location verification
• Caregiver and client identification`
      },
      {
        title: 'Importing Sandata Files',
        body: `1. Navigate to **EVV Inbox** → **Import** tab
2. Click **"Upload Sandata File"**
3. Select your CSV or Excel file from Sandata
4. Review the import preview
5. Click **"Import"**

The system will automatically:
• Match visits to clients and caregivers
• Flag any exceptions or discrepancies
• Create records for review`
      },
      {
        title: 'EVV Exceptions',
        body: `Exceptions are flagged when EVV data doesn't match scheduled visits:

**Exception Types:**
• **Time Variance** - Check-in/out differs from scheduled time
• **Location Variance** - GPS location doesn't match client address
• **Missing EVV** - Scheduled visit has no EVV record
• **Unmatched EVV** - EVV record doesn't match any scheduled visit

**Resolving Exceptions:**
1. Review the exception details
2. Add notes explaining the variance
3. Change status to "Reviewed" or "Resolved"
4. For legitimate issues, update the visit record`
      },
      {
        title: 'EVV Exception Workflow',
        body: `**Status Flow:**
1. **Pending** - New exception, needs review
2. **Under Review** - Being investigated
3. **Notified** - Caregiver/supervisor notified (triggers email if enabled)
4. **Resolved** - Issue addressed and documented
5. **Escalated** - Requires management attention

**Best Practice:** Review and resolve exceptions within 48 hours.`
      }
    ]
  },
  {
    id: 'documents',
    title: 'Document Library',
    icon: FileText,
    content: [
      {
        title: 'Document Types',
        body: `The system tracks these document types:

**Client Documents:**
• Care Plans
• Assessments
• Physician Orders
• Consent Forms
• Emergency Contacts

**Personnel Documents:**
• Background Checks
• Training Certificates
• Licenses/Certifications
• Employment Records`
      },
      {
        title: 'Uploading Documents',
        body: `1. Navigate to **Documents**
2. Click **"Upload Document"**
3. Select the document type
4. Choose the associated client or personnel
5. Select your file (PDF, DOC, DOCX, JPG, PNG)
6. Add expiration date if applicable
7. Click **"Upload"**

**Tip:** Name files descriptively (e.g., "Smith_John_CarePlan_2025.pdf")`
      },
      {
        title: 'Document Expiration',
        body: `The system tracks document expiration dates:

• **Green** - Valid, not expiring soon
• **Yellow** - Expiring within 30 days
• **Red** - Expired

**Notifications:** Compliance Managers receive alerts for expiring documents.`
      },
      {
        title: 'Document Versioning',
        body: `When you upload a new version of an existing document:

1. The old version is archived (not deleted)
2. The new version becomes current
3. Version history is maintained for audit purposes

Access version history from the document detail page.`
      }
    ]
  },
  {
    id: 'survey-readiness',
    title: 'Survey Readiness',
    icon: ClipboardCheck,
    content: [
      {
        title: 'Survey Dashboard',
        body: `Navigate to **Survey Dashboard** to see your agency's compliance status.

The dashboard shows:
• Overall readiness score
• Missing documentation by category
• Expiring items
• Action items for compliance`
      },
      {
        title: 'Generating Evidence Packs',
        body: `Create documentation packages for DHS surveys:

**Client Evidence Pack:**
1. Go to Survey Dashboard
2. Click **"Export Client Pack"**
3. Select the client
4. Choose date range
5. Click **"Generate"**

**Agency Evidence Pack:**
1. Click **"Export Agency Pack"**
2. Select categories to include
3. Click **"Generate"**

Packs are downloaded as ZIP files containing organized documentation and an index.`
      },
      {
        title: 'Evidence Pack Contents',
        body: `**Client Pack includes:**
• Client documents (care plan, assessments, etc.)
• Visit records for date range
• EVV verification data
• Exception resolutions

**Agency Pack includes:**
• Active client summary
• Personnel training matrix
• EVV exception aging report
• Documentation compliance report`
      },
      {
        title: 'Preparing for a Survey',
        body: `**Weekly Tasks:**
1. Review Survey Dashboard for alerts
2. Address any expired documents
3. Resolve pending EVV exceptions
4. Generate test evidence packs

**Before Survey:**
1. Generate fresh evidence packs
2. Review all flagged items
3. Ensure all client files are complete
4. Verify personnel training is current`
      }
    ]
  },
  {
    id: 'user-management',
    title: 'User Management',
    icon: UserPlus,
    content: [
      {
        title: 'Adding Users (Admin Only)',
        body: `1. Navigate to **Users**
2. Click **"Add User"**
3. Enter user details:
   - Email address
   - First and Last name
   - Role (Admin, Compliance Manager, Scheduler, Caregiver)
4. Set temporary password
5. Click **"Create"**

**Important:** Instruct new users to change their password on first login.`
      },
      {
        title: 'User Roles Explained',
        body: `**Admin:**
• Full system access
• User management
• System settings
• Audit logs

**Compliance Manager:**
• Survey readiness
• Document management
• EVV review
• Training matrix
• System settings (read-only)

**Scheduler:**
• Client management
• Visit scheduling
• EVV inbox
• Documents

**Caregiver:**
• View assigned schedule
• Limited access`
      },
      {
        title: 'Deactivating Users',
        body: `To deactivate a user (instead of deleting):

1. Go to **Users**
2. Find the user
3. Click **"Edit"**
4. Toggle **"Active"** to off
5. Save changes

**Note:** Deactivated users cannot log in but their records are retained for audit purposes.`
      }
    ]
  },
  {
    id: 'audit-logs',
    title: 'Audit Logs',
    icon: Shield,
    content: [
      {
        title: 'Viewing Audit Logs',
        body: `Navigate to **Audit Logs** (Admin/Compliance Manager only).

Audit logs track all system activity:
• User logins
• Data changes
• Document uploads
• Export generation
• Permission denials`
      },
      {
        title: 'Filtering Logs',
        body: `Use filters to find specific activities:

• **Date Range** - Select start and end dates
• **Action Type** - Login, Create, Update, Delete, Export
• **User** - Filter by specific user
• **Entity** - Client, Visit, Document, etc.`
      },
      {
        title: 'Audit Log Retention',
        body: `All audit logs are retained permanently for compliance purposes.

Logs cannot be edited or deleted - this ensures audit integrity for DHS surveys.`
      }
    ]
  },
  {
    id: 'system-settings',
    title: 'System Settings',
    icon: Settings,
    content: [
      {
        title: 'Viewing System Settings',
        body: `Navigate to **System Settings** (Admin/Compliance Manager only).

This page shows current configuration:
• Email notification status
• Authentication settings
• Export retention settings
• Rate limiting configuration`
      },
      {
        title: 'Manual Export Cleanup (Admin Only)',
        body: `Old survey exports are automatically cleaned up after 20 days.

To manually trigger cleanup:
1. Go to System Settings
2. Click **"Run Manual Cleanup"**
3. Confirm the action

This removes expired export files while maintaining audit logs.`
      }
    ]
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: XCircle,
    content: [
      {
        title: 'Login Issues',
        body: `**Can't log in?**
1. Verify your email is correct
2. Use the eye icon to check your password
3. Check for caps lock
4. Try the "Forgot Password" link

**Account locked?**
Contact your administrator - accounts may be locked after multiple failed attempts.`
      },
      {
        title: 'Missing Features',
        body: `**Can't see certain menu items?**
Your access depends on your role. Contact your administrator if you need additional access.

**Feature not working?**
1. Try refreshing the page (Ctrl+Shift+R)
2. Clear your browser cache
3. Try a different browser
4. Contact your administrator`
      },
      {
        title: 'Document Upload Errors',
        body: `**Upload failed?**
• Check file size (max 10MB)
• Verify file type is supported (PDF, DOC, DOCX, JPG, PNG)
• Ensure you have a stable internet connection
• Try a different browser`
      },
      {
        title: 'Getting Help',
        body: `For technical support:
1. Note the error message (if any)
2. Note what you were trying to do
3. Contact your system administrator

For compliance questions, contact your Compliance Manager.`
      }
    ]
  }
];

const KnowledgeBasePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState(['getting-started']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const filteredSections = sections.map(section => ({
    ...section,
    content: section.content.filter(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.body.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.content.length > 0 || section.title.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Layout>
      <div className="space-y-6" data-testid="knowledge-base-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Book className="h-6 w-6 text-avida-blue" />
              Knowledge Base
            </h1>
            <p className="text-slate-600 mt-1">
              Instruction manual and help documentation
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search documentation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="knowledge-base-search"
            />
          </div>
        </div>

        {/* Quick Navigation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Quick Navigation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {sections.map(section => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => {
                      setExpandedSections([section.id]);
                      document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-avida-blue hover:text-white transition-colors text-sm"
                  >
                    <Icon className="h-4 w-4" />
                    {section.title}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Documentation Sections */}
        <div className="space-y-4">
          {filteredSections.map(section => {
            const Icon = section.icon;
            const isExpanded = expandedSections.includes(section.id);
            
            return (
              <Card key={section.id} id={section.id}>
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleSection(section.id)}
                >
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-avida-blue/10">
                        <Icon className="h-5 w-5 text-avida-blue" />
                      </div>
                      <span>{section.title}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                  </CardTitle>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-6">
                      {section.content.map((item, index) => (
                        <div key={index} className="border-l-2 border-avida-blue/20 pl-4">
                          <h3 className="font-semibold text-slate-900 mb-2">
                            {item.title}
                          </h3>
                          <div className="text-slate-600 text-sm whitespace-pre-line leading-relaxed">
                            {item.body.split('\n').map((line, i) => {
                              // Bold text between **
                              const parts = line.split(/(\*\*.*?\*\*)/g);
                              return (
                                <p key={i} className={line.startsWith('•') || line.startsWith('-') || line.match(/^\d\./) ? 'ml-4' : ''}>
                                  {parts.map((part, j) => {
                                    if (part.startsWith('**') && part.endsWith('**')) {
                                      return <strong key={j} className="text-slate-900">{part.slice(2, -2)}</strong>;
                                    }
                                    return part;
                                  })}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <Card className="bg-slate-50">
          <CardContent className="py-4">
            <p className="text-sm text-slate-600 text-center">
              <strong>Avida Personal Care</strong> - Compliance & Operations Platform
              <br />
              <span className="text-slate-500">Wisconsin DHS Personal Care Provider Compliance</span>
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default KnowledgeBasePage;
